import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Dropdown, Tag, Switch, message, Input, InputNumber, Upload, Spin, Radio } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, PoweroffOutlined, MoreOutlined, UploadOutlined } from "@ant-design/icons";

import {
  getParentingVideosApi,
  createParentingVideoApi,
  updateParentingVideoApi,
  deleteParentingVideoApi,
  uploadImageApi,
  deleteImageApi,
} from "../../services/api";
import AppTable from "../../common/AppTable";
import AppInput from "../../common/FormInputs/AppInput";

const ACCENT = "#534ab7";

// Extracts the 11-char YouTube video ID from watch/shorts/youtu.be/embed URLs.
function extractYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtube\.com\/shorts\/|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  return m ? m[1] : null;
}

export default function ParentingVideos() {
  const [videos,   setVideos]   = useState([]);
  const [open,      setOpen]      = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [videoType, setVideoType] = useState("YOUTUBE");
  const [videoUrl,  setVideoUrl]  = useState("");
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();

  const fetchVideos = async () => {
    try {
      const res = await getParentingVideosApi();
      setVideos((res.data.data || []).slice().sort((a, b) => b.id - a.id));
    } catch {
      message.error("Failed to load parenting videos");
    }
  };

  useEffect(() => { fetchVideos(); }, []);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    setVideoType("YOUTUBE");
    setVideoUrl("");
    form.setFieldsValue({ active: true, sortOrder: 0 });
    setOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setVideoType(record.videoType || "YOUTUBE");
    setVideoUrl(record.videoUrl || "");
    form.setFieldsValue({
      title:     record.title,
      sortOrder: record.sortOrder,
      active:    record.active,
    });
    setOpen(true);
  };

  const handleVideoUpload = (file) => {
    (async () => {
      setUploading(true);
      try {
        const res = await uploadImageApi(file, "parenting-videos");
        const url = res?.data?.data;
        if (url) setVideoUrl(url);
      } catch {
        message.error("Video upload failed");
      } finally {
        setUploading(false);
      }
    })();
    return false;
  };

  const handleRemoveVideo = async () => {
    if (videoType === "UPLOAD" && videoUrl) {
      try { await deleteImageApi(videoUrl); } catch { /* ignore */ }
    }
    setVideoUrl("");
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    if (!videoUrl) { message.error("Add a video file or YouTube link"); return; }
    if (videoType === "YOUTUBE" && !extractYouTubeId(videoUrl)) {
      message.error("Enter a valid YouTube link");
      return;
    }

    const payload = { ...values, videoType, videoUrl };
    setLoading(true);
    try {
      if (editing) {
        await updateParentingVideoApi(editing.id, payload);
        message.success("Video updated");
      } else {
        await createParentingVideoApi(payload);
        message.success("Video added");
      }
      setOpen(false);
      fetchVideos();
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to save video");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (record) => {
    try {
      await updateParentingVideoApi(record.id, { ...record, active: !record.active });
      message.success(record.active ? "Deactivated" : "Activated");
      fetchVideos();
    } catch {
      message.error("Failed to update status");
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete this video?",
      content: "It will be removed from the mobile Parenting carousel.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteParentingVideoApi(id);
          message.success("Video deleted");
          fetchVideos();
        } catch {
          message.error("Failed to delete video");
        }
      },
    });
  };

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      width: 60,
      render: (v) => <span style={{ color: "#aaa", fontSize: 12 }}>#{v}</span>,
    },
    {
      title: "Title",
      dataIndex: "title",
      render: (v) => v || <span style={{ color: "#ccc" }}>—</span>,
    },
    {
      title: "Source",
      dataIndex: "videoType",
      width: 100,
      align: "center",
      render: (v) => <Tag color={v === "YOUTUBE" ? "red" : "blue"}>{v === "YOUTUBE" ? "YouTube" : "Upload"}</Tag>,
    },
    {
      title: "Sort",
      dataIndex: "sortOrder",
      width: 70,
      align: "center",
      render: (v) => <span style={{ color: "#6B7280", fontSize: 13 }}>{v}</span>,
    },
    {
      title: "Status",
      dataIndex: "active",
      width: 90,
      align: "center",
      render: (v) => <Tag color={v ? "success" : "default"}>{v ? "Active" : "Off"}</Tag>,
    },
    {
      title: "Actions",
      width: 70,
      align: "center",
      render: (_, r) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: () => openEdit(r) },
              {
                key: "toggle",
                label: r.active ? "Deactivate" : "Activate",
                icon: <PoweroffOutlined />,
                onClick: () => handleToggleActive(r),
              },
              { type: "divider" },
              { key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(r.id) },
            ],
          }}
        >
          <Button icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Parenting Videos</div>
          <div className="page-sub">{videos.length} videos — shown as a standalone carousel on the mobile Parenting Hub</div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ background: ACCENT, border: "none", borderRadius: 8 }}
          onClick={openAdd}
        >
          Add Video
        </Button>
      </div>

      <AppTable columns={columns} data={videos} />

      <Modal
        title={editing ? `Edit — ${editing.title || "Video"}` : "Add Parenting Video"}
        open={open}
        width={520}
        okText={editing ? "Save Changes" : "Add Video"}
        okButtonProps={{ style: { background: ACCENT, border: "none" }, loading }}
        onOk={handleSubmit}
        onCancel={() => { setOpen(false); setEditing(null); form.resetFields(); }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <AppInput name="title" label="Title (optional)" placeholder="e.g. Newborn Feeding Tips with Dr. Gupta" />

          <Form.Item label="Video Source">
            <Radio.Group
              value={videoType}
              onChange={(e) => { setVideoType(e.target.value); setVideoUrl(""); }}
              style={{ marginBottom: 10 }}
            >
              <Radio.Button value="YOUTUBE">YouTube Link</Radio.Button>
              <Radio.Button value="UPLOAD">Upload Video</Radio.Button>
            </Radio.Group>

            {videoType === "YOUTUBE" && (
              <Input
                placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
              />
            )}

            {videoType === "UPLOAD" && (
              videoUrl ? (
                <div style={{ position: "relative", display: "inline-block" }}>
                  <video
                    src={videoUrl}
                    controls
                    style={{ width: 220, height: 124, borderRadius: 10, border: "1px solid #eee" }}
                  />
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    style={{ position: "absolute", top: 4, right: 4, padding: "0 6px", minWidth: 0 }}
                    onClick={handleRemoveVideo}
                  />
                </div>
              ) : (
                <Upload accept="video/*" beforeUpload={handleVideoUpload} showUploadList={false} disabled={uploading}>
                  <Button icon={<UploadOutlined />} loading={uploading}>Upload Video</Button>
                </Upload>
              )
            )}
            {uploading && (
              <div style={{ marginTop: 8 }}><Spin size="small" /> <span style={{ fontSize: 12, color: "#888" }}>Uploading…</span></div>
            )}
          </Form.Item>

          <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
            <Form.Item name="sortOrder" label="Sort Order" style={{ width: 120 }}>
              <InputNumber min={0} max={999} style={{ width: "100%", borderRadius: 8 }} />
            </Form.Item>
            <Form.Item name="active" label="Active" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
